import { Suspense, createElement, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { WindowContext, useWindowShell, type WindowHandle } from './context'
import type { WindowNavigationState } from './navigation'
import type { WindowView } from './shell'

/**
 * Окно на своём адресе. Ключ и закрытие окно получает из `useWindow()`;
 * «откуда пришли» помнит ядро, а не страница (карточка 274).
 */
export function PageWindow({ kind }: { kind: string }) {
  const shell = useWindowShell()
  const { pathname } = useLocation()
  const view = shell.match(pathname)
  if (view === undefined || view.kind !== kind) return null
  return <PageWindowBody kind={kind} view={view} />
}

/** Тело окна: есть подходящий вид адреса, поэтому ручка окна всегда есть. */
function PageWindowBody({ kind, view }: { kind: string; view: WindowView }) {
  const shell = useWindowShell()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const commands = shell.useCommands()
  const returnTo = shell.useReturn(view.identity)
  const guard = useRef<(() => boolean) | null>(null)

  const handle = useMemo<WindowHandle>(() => {
    const handOver = (path: string, replace: boolean) => {
      commands.forgetReturn(view.identity)
      const state: WindowNavigationState = { windowFrom: returnTo ?? null }
      void navigate(path, { replace, state })
    }
    return {
      kind,
      identity: view.identity,
      presentation: 'page',
      params: view.params,
      setCloseGuard: (next) => {
        guard.current = next
      },
      // Закрытие не копит шаг истории: «назад» браузера не воскрешает закрытое окно.
      close: () => {
        if (guard.current !== null && !guard.current()) return
        const state: WindowNavigationState = { windowBack: true, windowTabSaved: true }
        void navigate(commands.close(`${pathname}${search}`), { replace: true, state })
      },
      replaceWith: (next, params) => handOver(shell.pathOf(next, params), true),
      proceedTo: (path) => handOver(path, false),
    }
  }, [commands, kind, navigate, pathname, returnTo, search, shell, view])

  const Content = shell.renderer(kind)
  if (Content === undefined) return null

  return (
    <WindowContext.Provider value={handle}>
      <Suspense fallback={<p role="status">Загрузка…</p>}>{createElement(Content, { key: view.identity })}</Suspense>
    </WindowContext.Provider>
  )
}
